/**
 * Dynamic Volume Pricing
 * Updates price display based on quantity and volume pricing breaks
 * 
 * Uses Custom Elements and event delegation pattern to automatically
 * re-attach listeners when HTML is replaced.
 */
(function () {

  function getCartQuantity(variantId, cart) {
    if (!cart?.items) return 0;
    return cart.items.reduce((total, item) => {
      return item.variant_id === variantId ? total + item.quantity : total;
    }, 0);
  }
  function getCurrentCart() {
    return new Promise(resolve => {
      if (window.currentCart) {
        resolve(window.currentCart);
        return;
      }
      fetch(`${window.themeVariables?.routes?.cartUrl || '/cart'}.js`).then(response => response.json()).then(cart => {
        window.currentCart = cart;
        resolve(cart);
      }).catch(() => resolve({
        items: []
      }));
    });
  }
  function formatPrice(cents, currencyEnabled, currencyCode) {
    let formatted = typeof formatMoney === 'function' ? formatMoney(cents) : (cents / 100).toFixed(2);
    if (currencyEnabled && currencyCode) {
      formatted = `${formatted} ${currencyCode}`;
    }
    return formatted;
  }
  function updatePrice(priceListElement, quantity) {
    const pricingDataScript = priceListElement.querySelector('[data-pricing-data]');
    if (!pricingDataScript) return;
    try {
      const pricingData = JSON.parse(pricingDataScript.textContent);
      const variantId = pricingData.variantId;
      getCurrentCart().then(cart => {
        const cartQuantity = getCartQuantity(variantId, cart);
        const totalQuantity = cartQuantity + quantity;
        let selectedPricePerUnit = pricingData.basePrice;
        if (pricingData.breaks?.length) {
          for (let i = pricingData.breaks.length - 1; i >= 0; i--) {
            if (totalQuantity >= pricingData.breaks[i].minimumQuantity) {
              selectedPricePerUnit = pricingData.breaks[i].price;
              break;
            }
          }
        }
        const totalPrice = selectedPricePerUnit * quantity;
        const priceElement = priceListElement.querySelector('[data-price-element]');
        if (priceElement) {
          const visuallyHidden = priceElement.querySelector('.visually-hidden');
          const formattedPrice = formatPrice(totalPrice, pricingData.currencyEnabled, pricingData.currencyCode);
          priceElement.innerHTML = visuallyHidden ? visuallyHidden.outerHTML + formattedPrice : formattedPrice;
        }
        const comparePriceElement = priceListElement.querySelector('[data-compare-price-element]');
        if (comparePriceElement) {
          const totalComparePrice = pricingData.compareAtPrice * quantity;
          if (pricingData.compareAtPrice > selectedPricePerUnit && pricingData.compareAtPrice > 0) {
            const visuallyHidden = comparePriceElement.querySelector('.visually-hidden');
            const formattedComparePrice = formatPrice(totalComparePrice, pricingData.currencyEnabled, pricingData.currencyCode);
            comparePriceElement.innerHTML = visuallyHidden ? visuallyHidden.outerHTML + formattedComparePrice : formattedComparePrice;
            comparePriceElement.style.display = '';
          } else {
            comparePriceElement.style.display = 'none';
          }
        }
      });
    } catch (error) {
      console.error('[Dynamic Pricing] Error updating price:', error);
    }
  }
  function updatePackHelper(container, quantity) {
    if (!container) return;
    container.querySelectorAll('[data-pack-helper]').forEach(helperElement => {
      const packSize = parseInt(helperElement.getAttribute('data-pack-size'), 10);
      if (!Number.isFinite(packSize) || packSize <= 0) return;
      const validatedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      const totalUnits = validatedQuantity * packSize;
      const singularUnitLabel = helperElement.getAttribute('data-unit-label') || 'Unit';
      const pluralUnitLabel = helperElement.getAttribute('data-unit-label-plural') || `${singularUnitLabel}s`;
      const resolvedUnitLabel = validatedQuantity > 1 ? pluralUnitLabel : singularUnitLabel;
      const quantityElement = helperElement.querySelector('[data-pack-helper-quantity]');
      const totalElement = helperElement.querySelector('[data-pack-helper-total]');
      const unitElement = helperElement.querySelector('[data-pack-helper-unit]');
      if (quantityElement) quantityElement.textContent = String(validatedQuantity);
      if (totalElement) totalElement.textContent = String(totalUnits);
      if (unitElement) unitElement.textContent = resolvedUnitLabel;
    });
  }
  function getValidatedQuantity(input, {
    clamp = true
  } = {}) {
    const rawValue = input.value.trim();
    if (rawValue === '' || isNaN(rawValue)) return null;
    let quantity = parseInt(rawValue);
    const minValue = parseInt(input.getAttribute('min')) || 1;
    const maxValue = input.getAttribute('max') ? parseInt(input.getAttribute('max')) : null;
    if (isNaN(quantity) || quantity < 1) {
      if (clamp) {
        quantity = minValue;
        input.value = quantity;
      } else {
        return null;
      }
    } else if (clamp && maxValue && quantity > maxValue) {
      quantity = maxValue;
      input.value = quantity;
    }
    return quantity;
  }
  class DynamicPricingManager {
    constructor() {
      this.inputDebounceTimers = new WeakMap();
      this.setupEventDelegation();
      this.setupVariantListeners();
      this.setupCartListeners();
      this.updateAllPrices();
    }
    setupEventDelegation() {
      document.addEventListener('click', event => {
        const button = event.target.closest('.quantity-selector__button');
        if (!button) return;
        const quantitySelector = button.closest('.quantity-selector');
        const quantityInput = quantitySelector?.querySelector('.quantity-selector__input');
        if (quantityInput) {
          setTimeout(() => {
            const quantity = parseInt(quantityInput.value) || 1;
            this.updatePricesForInput(quantityInput, quantity);
          }, 50);
        }
      });
      document.addEventListener('change', event => {
        if (!event.target.matches('.quantity-selector__input')) return;
        const quantity = getValidatedQuantity(event.target);
        if (quantity !== null) {
          this.updatePricesForInput(event.target, quantity);
        }
      });
      document.addEventListener('input', event => {
        if (!event.target.matches('.quantity-selector__input')) return;
        clearTimeout(this.inputDebounceTimers.get(event.target));
        const timer = setTimeout(() => {
          const quantity = getValidatedQuantity(event.target, {
            clamp: false
          });
          if (quantity !== null) {
            this.updatePricesForInput(event.target, quantity);
          }
        }, 300);
        this.inputDebounceTimers.set(event.target, timer);
      });
      document.addEventListener('blur', event => {
        if (!event.target.matches('.quantity-selector__input')) return;
        clearTimeout(this.inputDebounceTimers.get(event.target));
        const quantity = getValidatedQuantity(event.target);
        if (quantity !== null) {
          this.updatePricesForInput(event.target, quantity);
        }
      }, true);
      document.addEventListener('paste', event => {
        if (!event.target.matches('.quantity-selector__input')) return;
        setTimeout(() => {
          const quantity = getValidatedQuantity(event.target, {
            clamp: false
          });
          if (quantity !== null) {
            this.updatePricesForInput(event.target, quantity);
          }
        }, 10);
      });
    }
    setupVariantListeners() {
      const runAfterVariantChange = () => {
        requestAnimationFrame(() => {
          setTimeout(() => this.updateAllPrices(), 100);
        });
      };
      document.addEventListener('variant:changed', runAfterVariantChange);
      document.addEventListener('variant:change', runAfterVariantChange);
    }
    setupCartListeners() {
      document.addEventListener('cart:updated', event => {
        if (event.detail?.cart) {
          window.currentCart = event.detail.cart;
        }
        this.updateAllPrices();
      });
    }
    updatePricesForInput(quantityInput, quantity) {
      const container = quantityInput.closest('product-rerender') || quantityInput.closest('form') || quantityInput.closest('.product-form');
      if (!container) return;
      container.querySelectorAll('[data-dynamic-price]').forEach(priceList => {
        updatePrice(priceList, quantity);
      });
      updatePackHelper(container, quantity);
    }
    updateAllPrices() {
      document.querySelectorAll('[data-dynamic-price]').forEach(priceList => {
        const container = priceList.closest('product-rerender') || priceList.closest('form') || priceList.closest('.product-form');
        if (!container) return;
        const quantityInput = container.querySelector('.quantity-selector__input');
        let quantity = 1;
        if (quantityInput) {
          const parsed = parseInt(quantityInput.value);
          const minValue = parseInt(quantityInput.getAttribute('min')) || 1;
          quantity = !isNaN(parsed) && parsed >= minValue ? parsed : minValue;
        }
        updatePrice(priceList, quantity);
      });
      document.querySelectorAll('.quantity-selector__input').forEach(quantityInput => {
        const container = quantityInput.closest('product-rerender') || quantityInput.closest('form') || quantityInput.closest('.product-form');
        if (!container) return;
        const parsed = parseInt(quantityInput.value);
        const minValue = parseInt(quantityInput.getAttribute('min')) || 1;
        const quantity = !isNaN(parsed) && parsed >= minValue ? parsed : minValue;
        updatePackHelper(container, quantity);
      });
    }
  }
  const pricingManager = new DynamicPricingManager();
  document.addEventListener('product:rerender', () => {
    requestAnimationFrame(() => {
      setTimeout(() => pricingManager.updateAllPrices(), 100);
    });
  });
  document.addEventListener('product-rerender:rerender', () => {
    requestAnimationFrame(() => {
      setTimeout(() => pricingManager.updateAllPrices(), 100);
    });
  });
  document.addEventListener('openable-element:load:end', event => {
    const element = event.target;
    if (element.matches?.('quick-buy-drawer, quick-buy-popover') || element.closest?.('quick-buy-drawer, quick-buy-popover')) {
      requestAnimationFrame(() => pricingManager.updateAllPrices());
    }
  }, true);
})();

const personalisationEngineInstance = customElements.get('personalisation-engine');
if (!personalisationEngineInstance) {
  class PersonalisationEngine extends HTMLElement {
    static get observedAttributes() {
      return ['product-handle', 'product-id', 'section-id', 'form-id'];
    }
    constructor() {
      super();
      this._state = this._createInitialState();
      this._subscribers = new Set();
      this._abortController = null;
      this._touchedOptions = new Set();
      this._optionCount = 0;
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onStepToggle = this._onStepToggle.bind(this);
      this._onQuantityChange = this._onQuantityChange.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onSwatchKeyboardActivate = this._onSwatchKeyboardActivate.bind(this);
      this._onQuantityEnterKeydown = this._onQuantityEnterKeydown.bind(this);
      this._a11yLiveRegion = null;
    }
    connectedCallback() {
      if (window.matchMedia('(max-width: 999px)').matches) return;
      this._abortController = new AbortController();
      const signal = this._abortController.signal;
      this._cacheDOM();
      const init = () => {
        if (signal.aborted) return;
        this._bindEvents(signal);
        this._hydrateStateFromDOM();
        this._render();
        this._goToStep(1);
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, {
          once: true
        });
      } else {
        init();
      }
    }
    disconnectedCallback() {
      this._abortController?.abort();
      this._abortController = null;
      this._subscribers.clear();
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (name === 'product-handle' || name === 'product-id') {
        this._state = this._createInitialState();
        this._hydrateStateFromDOM();
        this._render();
      }
    }
    get productHandle() {
      return this.getAttribute('product-handle');
    }
    get productId() {
      return this.getAttribute('product-id');
    }
    get sectionId() {
      return this.getAttribute('section-id');
    }
    get formId() {
      return this.getAttribute('form-id');
    }
    get labelApplicationVariantId() {
      return this._state.labelApplicationVariantId || null;
    }
    get state() {
      return Object.freeze({
        ...this._state
      });
    }
    get canSubmit() {
      return !this._state.busy && !!this._state.variantId && this._state.variantAvailable && !!this._state.labelProductId && this._state.designSaved && this._state.quantityConfirmed && this._state.quantity >= this._state.quantityMin && this._state.consentAccepted;
    }

    // State Management
    _createInitialState() {
      this._touchedOptions?.clear();
      this._optionCount = 0;
      const steps = new Map();
      for (let i = 1; i <= 4; i++) {
        steps.set(i, {
          valid: false,
          visited: false,
          open: i === 1,
          data: {},
          errors: []
        });
      }
      return {
        activeStep: 1,
        variantId: null,
        variantAvailable: false,
        baseProductTitle: null,
        labelProductId: null,
        labelVariantId: null,
        labelPrice: 0,
        labelShortTitle: null,
        hasLabelApplication: false,
        labelApplicationVariantId: null,
        labelApplicationPrice: 0,
        attachToBaseProduct: false,
        designSaved: false,
        designProjectId: null,
        designImage3D: null,
        labelApplicationSelected: true,
        quantity: 1,
        quantityMin: 1,
        quantityMax: null,
        quantityStep: 1,
        quantityConfirmed: false,
        addonProductId: null,
        addonTitle: null,
        addonPrice: 0,
        addonQuantity: null,
        addonConfirmed: false,
        consentAccepted: false,
        consentError: false,
        busy: false,
        steps
      };
    }
    _setState(partial) {
      Object.assign(this._state, partial);
      this._notify();
      this._render();
    }
    _setStepState(step, partial) {
      const current = this._state.steps.get(step);
      if (!current) return;
      this._state.steps.set(step, {
        ...current,
        ...partial
      });
      this._notify();
      this._render();
    }
    subscribe(fn) {
      this._subscribers.add(fn);
      return () => this._subscribers.delete(fn);
    }
    _notify() {
      const snapshot = this.state;
      this._subscribers.forEach(fn => fn(snapshot));
    }
    _isStepComplete(step) {
      switch (step) {
        case 1:
          return !!this._state.variantId && this._state.variantAvailable;
        case 2:
          return !!this._state.labelProductId && this._state.designSaved;
        case 3:
          return this._state.quantityConfirmed && !!this._state.labelProductId && this._state.quantity >= this._state.quantityMin && (this._state.quantityMax === null || this._state.quantity <= this._state.quantityMax) && (this._state.quantity - this._state.quantityMin) % this._state.quantityStep === 0;
        case 4:
          return this._state.addonConfirmed;
        default:
          return false;
      }
    }

    // Validation
    _validateStep(step) {
      const errors = [];
      switch (step) {
        // Variant Selection
        case 1:
          if (!this._state.variantId) {
            errors.push('Please select a product variant.');
          } else if (!this._state.variantAvailable) {
            errors.push('The selected variant is currently out of stock.');
          }
          break;

        // Customisation Method (Labelling)
        case 2:
          if (!this._state.labelProductId) {
            errors.push('Please choose a customisation method.');
          } else if (!this._state.designSaved) {
            errors.push('Please complete your design before continuing.');
          }
          break;

        // Quantity & Options
        case 3:
          {
            if (!this._state.labelProductId) {
              errors.push('Please select a label before choosing a quantity.');
              break;
            }
            const qty = this._state.quantity;
            const {
              quantityMin,
              quantityMax,
              quantityStep
            } = this._state;
            if (qty < quantityMin) {
              errors.push(`Minimum order quantity is ${quantityMin}.`);
            }
            if (quantityMax !== null && qty > quantityMax) {
              errors.push(`Maximum order quantity is ${quantityMax}.`);
            }
            if ((qty - quantityMin) % quantityStep !== 0) {
              errors.push(`Quantity must be in increments of ${quantityStep}.`);
            }
            break;
          }
      }
      const valid = errors.length === 0;
      this._setStepState(step, {
        valid,
        errors
      });
      return valid;
    }
    _validateAll() {
      let allValid = true;
      for (let i = 1; i <= 4; i++) {
        if (!this._validateStep(i)) return false;
      }
      return allValid;
    }
    _renderErrors(step) {
      const stepState = this._state.steps.get(step);
      const container = this.querySelector(`[data-step-errors="${step}"]`);
      if (!container) return;
      container.innerHTML = '';
      container.hidden = stepState.errors.length === 0;
      stepState.errors.forEach(message => {
        const el = document.createElement('p');
        el.classList.add('per-flow-error-message');
        el.setAttribute('role', 'alert');
        el.textContent = message;
        container.appendChild(el);
      });
    }
    _clearErrors(step) {
      this._setStepState(step, {
        errors: [],
        valid: true
      });
      this._renderErrors(step);
    }
    _syncQuickBuyDrawerQuantity(drawer) {
      if (!drawer || !this._state.quantity) return;
      const qtyInput = drawer.querySelector('.quantity-selector__input') || drawer.querySelector('input[name="quantity"]');
      if (!qtyInput) return;
      const min = Number(qtyInput.getAttribute('min')) || 1;
      const maxAttr = qtyInput.getAttribute('max');
      const max = maxAttr ? Number(maxAttr) : null;
      const step = Number(qtyInput.getAttribute('step')) || 1;
      let qty = this._state.quantity;
      if (qty < min) qty = min;
      if (max !== null && !Number.isNaN(max) && qty > max) qty = max;
      if (step > 1) {
        qty = min + Math.floor((qty - min) / step) * step;
        if (qty < min) qty = min;
      }
      if (qtyInput.classList?.contains('quantity-selector__input') && 'quantity' in qtyInput) {
        qtyInput.quantity = qty;
      } else {
        qtyInput.value = String(qty);
      }
    }
    _bindQuickBuyFormRerenderSync(drawer) {
      const form = drawer.querySelector('form[is="product-form"]');
      if (!form) return;
      const ac = new AbortController();
      const {
        signal
      } = ac;
      const teardown = () => ac.abort();
      drawer.addEventListener('openable-element:close', teardown, {
        once: true
      });
      this._abortController?.signal.addEventListener('abort', teardown, {
        once: true
      });
      const bind = formEl => {
        if (!formEl) return;
        formEl.addEventListener('product:rerender', () => {
          queueMicrotask(() => {
            this._syncQuickBuyDrawerQuantity(drawer);
            const next = drawer.querySelector('form[is="product-form"]');
            if (next && next !== formEl) bind(next);
          });
        }, {
          signal
        });
      };
      bind(form);
    }
    _createDesignSetId() {
      const now = new Date();
      const yyyy = String(now.getFullYear());
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      const sec = String(now.getSeconds()).padStart(2, '0');
      const random = crypto.randomUUID().replace(/-/g, '').slice(0, 6).toUpperCase();
      return `SET-${yyyy}${mm}${dd}-${hh}${min}${sec}-${random}`;
    }

    // Add/Update Cart
    _buildCartPayload() {
      const items = [];
      const setId = this._createDesignSetId();

      // Base product
      if (this._state.variantId) {
        const baseProperties = {
          '_personalisation_set_id': setId,
          '_personalisation_set_item': 'base'
        };
        if (this._state.labelApplicationSelected) {
          baseProperties['Label application'] = 'Yes';
          baseProperties['_label_application'] = `gid://shopify/ProductVariant/${this.labelApplicationVariantId}`;
          baseProperties['_labelling_service_added'] = 'true';
          const priceCents = Math.round(Number(this._state.labelApplicationPrice) || 0);
          baseProperties['_labelling_service_price'] = String(priceCents);
        }
        items.push({
          id: this._state.variantId,
          quantity: this._state.quantity,
          properties: baseProperties
        });
      }
      if (this._state.labelVariantId) {
        const labelProperties = {
          '_personalisation_set_id': setId,
          '_personalisation_set_item': 'label'
        };
        if (this._state.designProjectId) {
          labelProperties['_pacdora_project_id'] = this._state.designProjectId;
        }
        if (this._state.designImage3D) {
          labelProperties['_pacdora_image3D'] = this._state.designImage3D;
        }
        const labelItem = {
          id: this._state.labelVariantId,
          quantity: this._state.quantity,
          properties: labelProperties
        };

        // Nest the label under the base product when required (e.g. heat transfer labels)
        if (this._state.attachToBaseProduct && this._state.variantId) {
          labelItem.parent_id = this._state.variantId;
        }
        items.push(labelItem);
      }
      if (this._state.addonProductId) {
        items.push({
          id: this._state.addonProductId,
          quantity: this._state.addonQuantity || this._state.quantity,
          properties: {
            '_personalisation_set_id': setId,
            '_personalisation_set_item': 'addon'
          }
        });
      }
      return {
        items
      };
    }
    async _addToCart() {
      if (this._state.busy) return;
      if (!this._validateAll()) {
        for (let i = 1; i <= 4; i++) {
          if (!this._state.steps.get(i).valid) {
            this._goToStep(i);
            this._scrollToError(i);
            break;
          }
        }
        return;
      }
      if (!this._state.consentAccepted) {
        this._setState({
          consentError: true
        });
        this._scrollToError('summary');
        return;
      }
      this._setState({
        busy: true
      });
      try {
        const payload = this._buildCartPayload();
        const cartAddUrl = window.themeVariables?.routes?.cartAddUrl || '/cart/add';
        const response = await fetch(`${cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.description || 'Could not add to cart.');
        }
        const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart';
        window.location.href = cartUrl;
      } catch (error) {
        this._setStepState(4, {
          errors: [error.message]
        });
        this._renderErrors(4);
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: {
            status: 'error',
            error: error.message
          }
        }));
      } finally {
        this._setState({
          busy: false
        });
      }
    }

    // Update rest of the theme dependent on the cart
    async _dispatchCartRefresh() {
      try {
        const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart';
        const response = await fetch(`${cartUrl}.js`);
        const cart = await response.json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart,
            openMiniCart: window.themeVariables?.settings?.cartType === 'drawer'
          }
        }));
      } catch (_) {
        // Suppress errors here since the item has already been added to the cart
      }
    }

    // Fetching & Rendering HTML
    async _fetchSection(sectionId, params = {}) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
      const response = await fetch(url.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) {
        throw new Error(`Section fetch failed (${response.status})`);
      }
      const html = await response.text();
      return document.createRange().createContextualFragment(html);
    }
    async _rerenderFromServer(params = {}) {
      try {
        this._setState({
          busy: true
        });

        // Replace with new HTML
        const fragment = await this._fetchSection(this.sectionId, params);
        const incoming = fragment.querySelector('personalisation-engine');
        if (incoming) {
          this.innerHTML = incoming.innerHTML;
        }

        // Re-cache and re-bind to the new HTML
        this._cacheDOM();
        this._hydrateStateFromDOM();
        this._render();
      } catch (error) {
        console.error('[PersonalisationEngine] rerender failed:', error);
      } finally {
        this._setState({
          busy: false
        });
      }
    }

    // DOM Helpers/Utils
    _cacheDOM() {
      this._accordionItems = this.querySelectorAll('[data-step]');
      this._quantityInput = this.querySelector('.per-flow-quantity-selector-number-input');
      this._addToCartButton = this.querySelector('[data-product-add-to-cart-button]');
      this._consentCheckbox = this.querySelector('input[name="consent"]');
      this._summaryContainer = this.querySelector('.product-summary');
      this._stepSubtitle3 = this.querySelector('[data-step-subtitle="3"]');
      this._volumePricingTable = this.querySelector('[data-volume-pricing-table]');

      // Summary table cells
      this._summaryBaseProduct = this.querySelector('[data-summary="base-product"]');
      this._summaryCustomisationMethod = this.querySelector('[data-summary="customisation-method"]');
      this._summaryCustomisationMethodPrice = this.querySelector('[data-summary="customisation-method-price"]');
      this._summaryLabelApplication = this.querySelector('[data-summary="label-application"]');
      this._summaryLabelApplicationPrice = this.querySelector('[data-summary="label-application-price"]');
      this._summaryQuantity = this.querySelector('[data-summary="quantity"]');
      this._summaryBaseProductPrice = this.querySelector('[data-summary="base-product-price"]');
      this._summaryAddon = this.querySelector('[data-summary="addon"]');
      this._summaryAddonPrice = this.querySelector('[data-summary="addon-price"]');
      this._summaryAddonQuantityRow = this.querySelector('[data-summary-row="addon-quantity"]');
      this._summaryAddonQuantity = this.querySelector('[data-summary="addon-quantity"]');
      this._summaryEstimatedTotal = this.querySelector('[data-summary="estimated-total"]');
      if (!this._a11yLiveRegion || !this.contains(this._a11yLiveRegion)) {
        const liveRegion = document.createElement('div');
        liveRegion.className = 'visually-hidden';
        liveRegion.setAttribute('aria-live', 'assertive');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('data-per-flow-live-region', '');
        this.appendChild(liveRegion);
        this._a11yLiveRegion = liveRegion;
      }
    }
    _hydrateStateFromDOM() {
      const form = document.forms[this.formId];
      if (form) {
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput?.value) {
          this._state.variantId = Number(variantInput.value);
        }
        const variantScript = this.querySelector('script[data-variant]');
        if (variantScript) {
          try {
            const variantData = JSON.parse(variantScript.textContent || '{}');
            this._state.variantAvailable = variantData.available ?? false;
            if (!this._state.baseProductTitle) {
              const productTitle = window.PRODUCT_DATA?.product?.title || '';
              const variantOptions = (variantData.options || []).join(' / ');
              this._state.baseProductTitle = variantOptions && variantOptions !== 'Default Title' ? `${productTitle} - ${variantOptions}` : productTitle;
            }
          } catch (_) {
            this._state.variantAvailable = false;
          }
        }
      }
      if (this._quantityInput?.value) {
        this._state.quantity = Number(this._quantityInput.value);
      }
      this._optionCount = this.querySelectorAll('.product-form__option-selector').length;
      if (!this.querySelector('[data-step="4"]')) {
        this._state.addonConfirmed = true;
      }
    }
    _bindEvents(signal) {
      const form = document.forms[this.formId];
      form?.addEventListener('variant:change', this._onVariantChange, {
        signal
      });
      form?.addEventListener('product:rerender', () => {
        this._cacheDOM();
        this._bindQuantityListeners(signal);
        this._render();
      }, {
        signal
      });
      form?.addEventListener('variant:changed', this._onVariantChange, {
        signal
      });
      this.addEventListener('change', event => {
        const optionInput = event.target.closest('[data-option-position]');
        if (optionInput) {
          this._touchedOptions.add(optionInput.getAttribute('data-option-position'));
        }
      }, {
        signal
      });
      this.addEventListener('click', this._onStepToggle, {
        signal
      });
      this.addEventListener('keydown', this._onSwatchKeyboardActivate, {
        signal
      });
      this.addEventListener('keydown', this._onQuantityEnterKeydown, {
        signal
      });
      this.addEventListener('click', event => {
        const nestedButton = event.target.closest('.per-flow-nested-button');
        if (!nestedButton) return;
        const isExpanding = nestedButton.getAttribute('aria-expanded') === 'true';
        if (!isExpanding) return;
        const parentItem = nestedButton.closest('[data-label-product-id]');
        if (!parentItem) return;
        const allLabelButtons = this.querySelectorAll('.per-flow-accordion-item[data-label-product-id] .per-flow-nested-button');
        allLabelButtons.forEach(btn => {
          if (btn !== nestedButton && btn.getAttribute('aria-expanded') === 'true') {
            btn.setAttribute('aria-expanded', 'false');
          }
        });
      }, {
        signal
      });
      this.addEventListener('click', event => {
        const labelItem = event.target.closest('[data-label-product-id]');
        if (!labelItem) return;
        const newLabelId = Number(labelItem.dataset.labelProductId);
        if (newLabelId === this._state.labelProductId) return;
        const newMin = Number(labelItem.dataset.quantityMin) || 1;
        const rawMax = labelItem.dataset.quantityMax;
        const newMax = rawMax ? Number(rawMax) : null;
        const newStep = Number(labelItem.dataset.quantityStep) || 1;
        const newLabelVariantId = Number(labelItem.dataset.labelVariantId) || null;
        const newLabelPrice = Number(labelItem.dataset.labelPrice) || 0;
        const newLabelShortTitle = labelItem.dataset.labelShortTitle || null;
        const newHasLabelApplication = labelItem.dataset.hasLabelApplication === 'true';
        const newLabelApplicationVariantId = labelItem.dataset.labelApplicationVariantId || null;
        const newLabelApplicationPrice = Number(labelItem.dataset.labelApplicationPrice) || 0;
        const labelCheckbox = labelItem.querySelector('.per-flow-label-checkbox');
        const isLabelAppChecked = labelCheckbox ? labelCheckbox.checked : false;
        this._setState({
          labelProductId: newLabelId,
          labelVariantId: newLabelVariantId,
          labelPrice: newLabelPrice,
          labelShortTitle: newLabelShortTitle,
          hasLabelApplication: newHasLabelApplication,
          labelApplicationVariantId: newLabelApplicationVariantId,
          labelApplicationPrice: newLabelApplicationPrice,
          attachToBaseProduct: labelItem.dataset.attachToBaseProduct === 'true',
          labelApplicationSelected: isLabelAppChecked,
          designSaved: false,
          designProjectId: null,
          designImage3D: null,
          quantityMin: newMin,
          quantityMax: newMax,
          quantityStep: newStep,
          quantity: newMin,
          quantityConfirmed: false
        });
        this._augmentVolumePrices();
        this._hideInvalidVolumeRows();
        this._clearErrors(2);
        this._clearErrors(3);
      }, {
        signal
      });
      document.addEventListener('pacdora:design-saved', event => {
        const {
          labelProductId,
          projectId,
          image3D
        } = event.detail || {};
        if (!labelProductId) return;
        if (labelProductId === this._state.labelProductId) {
          this._setState({
            designSaved: true,
            designProjectId: projectId || null,
            designImage3D: image3D || null
          });
          this._clearErrors(2);
          this._updateDesignButton(labelProductId);
          this._completeAndAdvance(2);
          this._injectDesignImageAsFirstMedia(image3D);
        }
      }, {
        signal
      });
      this._bindQuantityListeners(signal);
      this._addToCartButton?.addEventListener('click', this._onSubmit, {
        signal
      });
      this._consentCheckbox?.addEventListener('change', event => {
        this._setState({
          consentAccepted: event.target.checked,
          consentError: !event.target.checked
        });
      }, {
        signal
      });
      this.addEventListener('change', event => {
        const checkbox = event.target.closest('.per-flow-label-checkbox');
        if (!checkbox) return;
        this._setState({
          labelApplicationSelected: checkbox.checked
        });
      }, {
        signal
      });

      // Add-on product selection
      this.addEventListener('click', event => {
        const noAddonButton = event.target.closest('.per-flow-no-addon-item');
        if (noAddonButton) {
          this._setState({
            addonProductId: null,
            addonTitle: null,
            addonPrice: 0,
            addonQuantity: null,
            addonConfirmed: true
          });
          this._clearErrors(4);
          this._completeAndAdvance(4);
          return;
        }
        const addonItem = event.target.closest('[data-addon-product-id]');
        if (addonItem) {
          if (event.target.closest('[aria-controls*="-drawer"]') || event.target.closest('quick-buy-drawer')) return;
          const addonQuantity = this._state.quantity;
          const fallbackPrice = Number(addonItem.dataset.addonPrice) || 0;
          const priceContainer = addonItem.querySelector('[data-dynamic-price]');
          const addonPrice = this._resolveAddonUnitPrice(priceContainer, addonQuantity, fallbackPrice);
          this._setState({
            addonProductId: Number(addonItem.dataset.addonProductId),
            addonTitle: addonItem.dataset.addonTitle || null,
            addonPrice,
            addonQuantity,
            addonConfirmed: true
          });
          this._clearErrors(4);
          this._completeAndAdvance(4);
        }
      }, {
        signal
      });
      this.addEventListener('click', event => {
        const toggle = event.target.closest('[aria-controls*="-drawer"]');
        if (!toggle) return;
        const drawerId = toggle.getAttribute('aria-controls');
        const drawer = drawerId && this.querySelector(`#${drawerId}`);
        if (!drawer) return;
        drawer.addEventListener('openable-element:load:end', () => {
          this._syncQuickBuyDrawerQuantity(drawer);
          this._bindQuickBuyFormRerenderSync(drawer);
        }, {
          once: true
        });
      }, {
        signal
      });

      // Intercept quick-buy drawer form submissions for add-on products
      this.addEventListener('submit', event => {
        const form = event.target;
        const drawer = form.closest('quick-buy-drawer');
        if (!drawer) return;
        event.preventDefault();
        event.stopPropagation();
        const variantId = form.querySelector('input[name="id"]')?.value;
        if (variantId) {
          const drawerQtyInput = drawer.querySelector('input[name="quantity"]');
          let addonQuantity = drawerQtyInput ? Number(drawerQtyInput.value) : this._state.quantity;
          if (drawerQtyInput) {
            const min = Number(drawerQtyInput.getAttribute('min')) || 1;
            const max = drawerQtyInput.getAttribute('max') ? Number(drawerQtyInput.getAttribute('max')) : null;
            const step = Number(drawerQtyInput.getAttribute('step')) || 1;
            let invalid = false;
            if (isNaN(addonQuantity) || addonQuantity < min) invalid = true;
            if (max !== null && addonQuantity > max) invalid = true;
            if (step > 1 && (addonQuantity - min) % step !== 0) invalid = true;
            if (invalid) {
              drawerQtyInput.focus();
              drawerQtyInput.reportValidity();
              return;
            }
          }
          const productName = drawer.querySelector('.product-item-meta__title')?.textContent?.trim() || drawer.closest('[data-addon-product-id]')?.dataset.addonTitle || null;
          const variantPicker = drawer.querySelector('variant-picker');
          const selectedVariant = variantPicker?.selectedVariant;
          const variantLabel = selectedVariant?.title;
          let addonTitle = productName;
          if (addonTitle && variantLabel && variantLabel !== 'Default Title') {
            addonTitle = `${productName} - ${variantLabel}`;
          }
          const fallbackPrice = selectedVariant?.price || Number(drawer.closest('[data-addon-price]')?.dataset.addonPrice) || 0;
          const priceContainer = drawer.querySelector('[data-dynamic-price]');
          const addonPrice = this._resolveAddonUnitPrice(priceContainer, addonQuantity, fallbackPrice);
          this._setState({
            addonProductId: Number(variantId),
            addonTitle,
            addonPrice,
            addonQuantity,
            addonConfirmed: true
          });
          this._clearErrors(4);
        }
        drawer.open = false;
        this._completeAndAdvance(4);
      }, {
        capture: true,
        signal
      });
    }
    _bindQuantityListeners(signal) {
      this._quantityInput?.addEventListener('input', this._onQuantityChange, {
        signal
      });
      this._quantityInput?.addEventListener('change', this._onQuantityChange, {
        signal
      });
    }
    _onVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;
      const productTitle = window.PRODUCT_DATA?.product?.title || '';
      const variantOptions = (variant.options || []).join(' / ');
      const baseProductTitle = variantOptions && variantOptions !== 'Default Title' ? `${productTitle} - ${variantOptions}` : productTitle;
      const hadDesign = this._state.designSaved;
      this._setState({
        variantId: variant.id,
        variantAvailable: variant.available ?? false,
        baseProductTitle,
        designSaved: false,
        designProjectId: null,
        designImage3D: null
      });
      if (hadDesign) {
        this._resetDesignButtons();
      }
      this._clearErrors(1);
      if (this._touchedOptions.size >= this._optionCount) {
        this._completeAndAdvance(1);
      }
      this._updateVolumePricingTable(variant.id);
      this._updatePacdoraModel(variant.id);
    }
    async _updatePacdoraModel(variantId) {
      if (typeof updatePacdoraWithVariant !== 'function') return;
      const pacdoraVariant = window.PRODUCT_DATA?.variants?.find(v => v.id === variantId);
      if (!pacdoraVariant) return;
      await updatePacdoraWithVariant(pacdoraVariant);
      if (this._state.labelProductId && typeof switchToLabelScene === 'function') {
        await switchToLabelScene(this._state.labelProductId);
      }
    }
    _injectDesignImageAsFirstMedia(image3DUrl) {
      if (!image3DUrl) return;

      // === 1. Replace the CURRENTLY SELECTED main image (this part already worked) ===
      let mainImgReplaced = false;
      const selectedMediaItem = document.querySelector('.product__media-item.is-selected');
      if (selectedMediaItem) {
        const img = selectedMediaItem.querySelector('img');
        if (img) {
          if (!img.dataset.originalSrc) {
            img.dataset.originalSrc = img.src || '';
          }
          if (!img.dataset.originalSrcset) {
            img.dataset.originalSrcset = img.srcset || '';
          }
          img.src = image3DUrl;
          img.srcset = ''; // Clear srcset
          img.sizes = '(max-width: 999px) calc(100vw - 48px), 640px';
          img.alt = "Custom 3D Design Preview";
          img.loading = "eager";
          selectedMediaItem.dataset.isDesignImage = 'true';
          mainImgReplaced = true;
        }
      }

      // Fallback: replace first media item if no .is-selected found
      if (!mainImgReplaced) {
        const firstMediaItem = document.querySelector('.product__media-item:first-child');
        if (firstMediaItem) {
          const img = firstMediaItem.querySelector('img');
          if (img) {
            if (!img.dataset.originalSrc) img.dataset.originalSrc = img.src || '';
            if (!img.dataset.originalSrcset) img.dataset.originalSrcset = img.srcset || '';
            img.src = image3DUrl;
            img.srcset = '';
            img.sizes = '(max-width: 999px) calc(100vw - 48px), 640px';
            img.alt = "Custom 3D Design Preview";
            img.loading = "eager";
            firstMediaItem.dataset.isDesignImage = 'true';
          }
        }
      }

      // === 2. Replace the CURRENT ACTIVE thumbnail (the one with aria-current="true") ===
      const activeThumbnail = document.querySelector('.product__thumbnail-item[aria-current="true"]');
      if (activeThumbnail) {
        const thumbImg = activeThumbnail.querySelector('img');
        if (thumbImg) {
          if (!thumbImg.dataset.originalSrc) {
            thumbImg.dataset.originalSrc = thumbImg.src || '';
          }
          if (!thumbImg.dataset.originalSrcset) {
            thumbImg.dataset.originalSrcset = thumbImg.srcset || '';
          }
          thumbImg.src = image3DUrl;
          thumbImg.srcset = '';
          thumbImg.sizes = '72px';
          thumbImg.alt = "Custom 3D Design Preview";
          activeThumbnail.dataset.isDesignImage = 'true';
          activeThumbnail.setAttribute('aria-label', 'Custom 3D Design Preview');
        }
      }
      // Fallback: if no active thumbnail found, update the first one
      else {
        const firstThumb = document.querySelector('.product__thumbnail-list-inner .product__thumbnail-item:first-child');
        if (firstThumb) {
          const thumbImg = firstThumb.querySelector('img');
          if (thumbImg) {
            if (!thumbImg.dataset.originalSrc) thumbImg.dataset.originalSrc = thumbImg.src || '';
            if (!thumbImg.dataset.originalSrcset) thumbImg.dataset.originalSrcset = thumbImg.srcset || '';
            thumbImg.src = image3DUrl;
            thumbImg.srcset = '';
            thumbImg.sizes = '72px';
            thumbImg.alt = "Custom 3D Design Preview";
            firstThumb.dataset.isDesignImage = 'true';
            firstThumb.setAttribute('aria-label', 'Custom 3D Design Preview');
          }
        }
      }

      // === 3. Refresh Flickity ===
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        const carousel = document.querySelector('.flickity-enabled, flickity-carousel, [data-flickity]');
        if (carousel) {
          const flkty = Flickity.data(carousel) || carousel.flickityInstance || carousel._flickity;
          if (flkty && typeof flkty.resize === 'function') {
            flkty.resize();
          }
        }
      }, 100);
    }
    _onStepToggle(event) {
      const button = event.target.closest('.per-flow-button');
      if (!button) return;
      const item = button.closest('[data-step]');
      if (!item) return;
      const stepIndex = [...this._accordionItems].indexOf(item) + 1;
      if (stepIndex < 1) return;
      const isExpanding = button.getAttribute('aria-expanded') === 'true';

      // When collapsing step 3, confirm the quantity
      if (!isExpanding && stepIndex === 3 && this._state.steps.get(3)?.visited) {
        this._setState({
          quantityConfirmed: true
        });
      }
      this._setStepState(stepIndex, {
        visited: true
      });
      this._setState({
        activeStep: stepIndex
      });
    }
    _onQuantityChange(event) {
      const value = Number(event.target.value);
      if (Number.isNaN(value)) return;
      this._setState({
        quantity: value,
        quantityConfirmed: true
      });
      this._clearErrors(3);
    }
    _onSubmit(event) {
      event.preventDefault();
      this._addToCart();
    }
    _onSwatchKeyboardActivate(event) {
      const key = event.key;
      if (key !== 'Enter' && key !== ' ') return;
      const activator = event.target.closest('.color-swatch__item, .block-swatch__item, .variant-swatch__item');
      if (!activator) return;
      const isLabel = activator.tagName === 'LABEL';
      const isAnchor = activator.tagName === 'A';
      if (!isLabel && !(isAnchor && key === ' ')) return;
      event.preventDefault();
      activator.click();
    }
    _onQuantityEnterKeydown(event) {
      if (event.key !== 'Enter') return;
      const quantityInput = event.target.closest('.per-flow-quantity-selector-number-input');
      if (!quantityInput) return;
      event.preventDefault();
      event.stopPropagation();
    }
    _goToStep(step) {
      if (step < 1 || step > 4) return;
      const target = this._accordionItems?.[step - 1];
      const button = target?.querySelector('button.per-flow-button');
      if (button) {
        button.setAttribute('aria-expanded', 'true');
      }
      this._setState({
        activeStep: step
      });
      this._setStepState(step, {
        visited: true
      });
    }
    _updateDesignButton(labelProductId) {
      const button = this.querySelector(`.per-flow-button-add-to-design[data-label-product-id="${labelProductId}"]`);
      if (!button) return;
      const textEl = button.querySelector('span');
      if (textEl) {
        textEl.textContent = 'Edit design';
      }
      button.classList.add('per-flow-button-add-to-design--saved');
    }
    _resetDesignButtons() {
      const buttons = this.querySelectorAll('.per-flow-button-add-to-design');
      buttons.forEach(button => {
        button.classList.remove('per-flow-button-add-to-design--saved');
        const textEl = button.querySelector('span');
        if (textEl) {
          textEl.textContent = 'Add design';
        }
      });
    }
    async _updateVolumePricingTable(variantId) {
      if (!this._volumePricingTable) return;
      try {
        const fragment = await this._fetchSection(this.sectionId, {
          variant: variantId
        });
        const incoming = fragment.querySelector('[data-volume-pricing-table]');
        if (incoming) {
          this._volumePricingTable.innerHTML = incoming.innerHTML;
        }
        this._augmentVolumePrices();
        this._hideInvalidVolumeRows();
      } catch (error) {
        console.error('[PersonalisationEngine] volume pricing update failed:', error);
      }
    }

    // Adds label price onto the base product price in the volume pricing table
    _augmentVolumePrices() {
      if (!this._volumePricingTable) return;
      const labelPrice = this._state.labelPrice || 0;
      const priceCells = this._volumePricingTable.querySelectorAll('.volume-pricing-table__price[data-base-price]');
      if (!priceCells.length) return;
      const fmt = typeof formatMoney === 'function' ? cents => formatMoney(cents) : cents => (cents / 100).toFixed(2);
      const firstBasePrice = parseInt(priceCells[0].dataset.basePrice, 10);
      const firstCombined = firstBasePrice + labelPrice;
      priceCells.forEach((cell, index) => {
        const basePrice = parseInt(cell.dataset.basePrice, 10);
        const combined = basePrice + labelPrice;
        cell.textContent = fmt(combined);
        if (index > 0 && firstCombined > 0) {
          const row = cell.closest('[role="row"]');
          const savingsCell = row?.querySelector('.volume-pricing-table__savings');
          if (savingsCell) {
            const savingsPercent = ((firstCombined - combined) / firstCombined * 100).toFixed(1);
            const display = savingsPercent.endsWith('.0') ? savingsPercent.slice(0, -2) : savingsPercent;
            savingsCell.textContent = parseFloat(savingsPercent) > 0 ? `${display}%` : '';
          }
        }
      });
    }
    _hideInvalidVolumeRows() {
      if (!this._volumePricingTable) return;
      const minQty = this._state.quantityMin || 1;
      console.log('[hide rows] Effective min quantity:', minQty);
      const rows = this._volumePricingTable.querySelectorAll('.volume-pricing-table__body .volume-pricing-table__row');
      console.log('[hide rows] Found', rows.length, 'rows');
      rows.forEach((row, index) => {
        const qtyCell = row.querySelector('div[role="cell"]:first-child');
        if (!qtyCell) {
          console.warn('[hide rows] No quantity cell in row', index);
          return;
        }
        let text = qtyCell.textContent.trim();

        // Parse start/end for hiding logic
        let startQty = 0;
        let endQty = Infinity;

        // Clean for parsing (but keep original for display adjustment)
        const cleaned = text.replace(/Quantity range:/gi, '').replace(/from|qty|quantity|units|pcs|each/gi, '').replace(/\s+/g, ' ').trim();
        const rangeMatch = cleaned.match(/^(\d+)[\s\-–—]+\s*(\d+)$/);
        const plusMatch = cleaned.match(/^(\d+)\+$/);
        if (rangeMatch) {
          startQty = parseInt(rangeMatch[1], 10) || 0;
          endQty = parseInt(rangeMatch[2], 10) || Infinity;
        } else if (plusMatch) {
          startQty = parseInt(plusMatch[1], 10) || 0;
          endQty = Infinity;
        } else {
          const numMatch = cleaned.match(/\d+/);
          startQty = numMatch ? parseInt(numMatch[0], 10) : 0;
        }
        console.log('[hide rows] Row', index, 'parsed:', {
          raw: text,
          startQty,
          endQty: endQty === Infinity ? '∞' : endQty
        });
        const completelyBelow = endQty < minQty;
        row.style.display = completelyBelow ? 'none' : '';

        // Cleanup & optional start number adjustment for visible rows
        if (!completelyBelow) {
          let displayText = qtyCell.textContent;

          // Remove "Quantity range:" prefix
          displayText = displayText.replace(/Quantity range:/gi, '').trim();
          displayText = displayText.replace(/\s+/g, ' '); // clean up extra spaces

          // If starts below min → replace starting number
          if (startQty > 0 && startQty < minQty && endQty !== Infinity) {
            displayText = displayText.replace(new RegExp(`\\b${startQty}\\b`), minQty);
          }
          qtyCell.textContent = displayText;
        }
        console.log('[hide rows] Row', index, {
          startQty,
          endQty: endQty === Infinity ? '∞' : endQty,
          completelyBelow,
          finalDisplay: row.style.display || window.getComputedStyle(row).display
        });
      });
    }
    _getBaseUnitPrice(quantity) {
      if (!this._volumePricingTable) return 0;
      const priceCells = this._volumePricingTable.querySelectorAll('.volume-pricing-table__price[data-base-price]');
      if (!priceCells.length) return 0;
      let matchedPrice = parseInt(priceCells[0].dataset.basePrice, 10) || 0;
      for (const cell of priceCells) {
        const minQty = parseInt(cell.dataset.minQuantity, 10) || 0;
        if (quantity >= minQty) {
          matchedPrice = parseInt(cell.dataset.basePrice, 10) || 0;
        }
      }
      return matchedPrice;
    }
    _resolveAddonUnitPrice(container, quantity, fallbackPrice = 0) {
      if (!container) return fallbackPrice;
      const script = container.querySelector('[data-pricing-data]');
      if (!script) return fallbackPrice;
      try {
        const data = JSON.parse(script.textContent);
        let unitPrice = data.basePrice || fallbackPrice;
        if (data.breaks?.length) {
          for (let i = data.breaks.length - 1; i >= 0; i--) {
            if (quantity >= data.breaks[i].minimumQuantity) {
              unitPrice = data.breaks[i].price;
              break;
            }
          }
        }
        return unitPrice;
      } catch (e) {
        return fallbackPrice;
      }
    }
    _scrollToError(step) {
      const selector = step === 'summary' ? '[data-step-errors="summary"]' : `[data-step-errors="${step}"]`;
      const errorContainer = this.querySelector(selector);
      if (errorContainer) {
        errorContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        const focusTarget = errorContainer.querySelector('.per-flow-error-message') || errorContainer;
        if (!focusTarget.hasAttribute('tabindex')) {
          focusTarget.setAttribute('tabindex', '-1');
        }
        requestAnimationFrame(() => {
          focusTarget.focus({
            preventScroll: true
          });
        });
        const message = focusTarget.textContent?.trim();
        if (message && this._a11yLiveRegion) {
          this._a11yLiveRegion.textContent = '';
          requestAnimationFrame(() => {
            this._a11yLiveRegion.textContent = message;
          });
        }
      }
    }
    _repositionAfterAddonCollapse() {
      const stepThreeItem = this._accordionItems?.[2];
      const stepThreeButton = stepThreeItem?.querySelector('button.per-flow-button');
      if (!stepThreeButton) return;
      const topOffset = 16;
      const stepThreeTop = Math.max(0, Math.round(stepThreeButton.getBoundingClientRect().top + window.scrollY - topOffset));
      const currentScroll = window.scrollY;
      const delta = currentScroll - stepThreeTop;
      const minDelta = 120;
      const maxDelta = Math.max(800, Math.round(window.innerHeight * 1.5));
      if (delta < minDelta || delta > maxDelta) return;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: stepThreeTop,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }
    _render() {
      if (this._addToCartButton) {
        this._addToCartButton.toggleAttribute('disabled', this._state.busy);
        this._addToCartButton.setAttribute('aria-busy', String(this._state.busy));
      }
      for (let i = 1; i <= 4; i++) {
        this._renderErrors(i);
      }
      const hasLabel = this._state.labelProductId !== null;
      if (this._quantityInput) {
        this._quantityInput.toggleAttribute('disabled', !hasLabel);
        if (hasLabel) {
          const {
            quantityMin,
            quantityMax,
            quantityStep,
            quantity
          } = this._state;
          this._quantityInput.min = quantityMin;
          this._quantityInput.step = quantityStep;
          this._quantityInput.value = quantity;
          if (quantityMax !== null) {
            this._quantityInput.max = quantityMax;
          } else {
            this._quantityInput.removeAttribute('max');
          }
        }
      }
      if (this._stepSubtitle3) {
        this._stepSubtitle3.textContent = hasLabel ? `Minimum order: ${this._state.quantityMin} units` : 'Select a label to continue';
      }
      const summaryErrors = this.querySelector('[data-step-errors="summary"]');
      if (summaryErrors) {
        summaryErrors.innerHTML = '';
        summaryErrors.hidden = !this._state.consentError;
        if (this._state.consentError) {
          const el = document.createElement('p');
          el.classList.add('per-flow-error-message');
          el.setAttribute('role', 'alert');
          el.textContent = 'Please accept the terms and conditions to proceed.';
          summaryErrors.appendChild(el);
        }
      }
      this._accordionItems?.forEach((item, index) => {
        const step = index + 1;
        const complete = this._isStepComplete(step);
        item.toggleAttribute('data-step-completed', complete);
      });
      const noAddonBtn = this.querySelector('.per-flow-no-addon-item');
      if (noAddonBtn) {
        noAddonBtn.toggleAttribute('data-selected', this._state.addonConfirmed && !this._state.addonProductId);
      }
      const addonItems = this.querySelectorAll('[data-addon-product-id]');
      addonItems.forEach(item => {
        const id = Number(item.dataset.addonProductId);
        item.toggleAttribute('data-selected', this._state.addonConfirmed && id === this._state.addonProductId);
      });
      this._renderSummary();
    }
    _renderSummary() {
      const s = this._state;
      const dash = '-';
      if (this._summaryBaseProduct) {
        this._summaryBaseProduct.textContent = s.baseProductTitle || this._summaryBaseProduct.textContent;
      }
      if (this._summaryCustomisationMethod) {
        this._summaryCustomisationMethod.textContent = s.labelShortTitle || dash;
      }
      if (this._summaryCustomisationMethodPrice) {
        if (!s.labelProductId || !s.quantity || !s.labelPrice) {
          this._summaryCustomisationMethodPrice.textContent = '';
        } else {
          const totalLabelPrice = s.labelPrice * s.quantity;
          const fmt = typeof formatMoney === 'function' ? cents => formatMoney(cents) : cents => (cents / 100).toFixed(2);
          this._summaryCustomisationMethodPrice.textContent = fmt(totalLabelPrice);
        }
      }
      if (this._summaryQuantity) {
        this._summaryQuantity.textContent = s.labelProductId ? String(s.quantity) : dash;
      }
      if (this._summaryBaseProductPrice) {
        if (!s.labelProductId || !s.quantity) {
          this._summaryBaseProductPrice.textContent = '';
        } else {
          const baseUnitPrice = this._getBaseUnitPrice(s.quantity);
          const totalBasePrice = baseUnitPrice * s.quantity;
          const fmt = typeof formatMoney === 'function' ? cents => formatMoney(cents) : cents => (cents / 100).toFixed(2);
          this._summaryBaseProductPrice.textContent = fmt(totalBasePrice);
        }
      }
      if (this._summaryAddon) {
        if (!s.addonConfirmed) {
          this._summaryAddon.textContent = dash;
        } else {
          this._summaryAddon.textContent = s.addonTitle || 'None';
        }
      }
      if (this._summaryAddonPrice) {
        const hasAddon = s.addonConfirmed && s.addonProductId && s.addonPrice;
        if (!hasAddon) {
          this._summaryAddonPrice.textContent = '';
        } else {
          const addonQty = s.addonQuantity || s.quantity;
          const totalAddonPrice = s.addonPrice * addonQty;
          const fmt = typeof formatMoney === 'function' ? cents => formatMoney(cents) : cents => (cents / 100).toFixed(2);
          this._summaryAddonPrice.textContent = fmt(totalAddonPrice);
        }
      }
      if (this._summaryLabelApplication) {
        if (!s.labelProductId) {
          this._summaryLabelApplication.textContent = dash;
        } else if (!s.hasLabelApplication) {
          this._summaryLabelApplication.textContent = 'N/A';
        } else {
          this._summaryLabelApplication.textContent = s.labelApplicationSelected ? 'Yes' : 'No';
        }
      }
      if (this._summaryLabelApplicationPrice) {
        if (!s.labelApplicationSelected || !s.labelApplicationPrice || !s.quantity) {
          this._summaryLabelApplicationPrice.textContent = '';
        } else {
          const totalAppPrice = s.labelApplicationPrice * s.quantity;
          const fmt = typeof formatMoney === 'function' ? cents => formatMoney(cents) : cents => (cents / 100).toFixed(2);
          this._summaryLabelApplicationPrice.textContent = fmt(totalAppPrice);
        }
      }
      if (this._summaryAddonQuantityRow) {
        const hasAddon = s.addonConfirmed && s.addonProductId;
        this._summaryAddonQuantityRow.hidden = !hasAddon;
        if (this._summaryAddonQuantity) {
          this._summaryAddonQuantity.textContent = hasAddon ? String(s.addonQuantity || s.quantity) : dash;
        }
      }
      if (this._summaryEstimatedTotal) {
        if (!s.labelProductId || !s.quantity) {
          this._summaryEstimatedTotal.textContent = dash;
        } else {
          const qty = s.quantity;

          // Base product (volume-priced) * quantity
          const baseTotal = this._getBaseUnitPrice(qty) * qty;

          // Label product (flat per-unit) * quantity
          const labelTotal = (s.labelPrice || 0) * qty;

          // Label application (per-unit, only if checked)
          const labelAppTotal = s.labelApplicationSelected ? (s.labelApplicationPrice || 0) * qty : 0;

          // Add-on (volume-priced per-unit) * add-on quantity
          const addonTotal = s.addonConfirmed && s.addonProductId && s.addonPrice ? s.addonPrice * (s.addonQuantity || qty) : 0;
          const estimatedTotal = baseTotal + labelTotal + labelAppTotal + addonTotal;
          const fmt = typeof formatMoney === 'function' ? cents => formatMoney(cents) : cents => (cents / 100).toFixed(2);
          this._summaryEstimatedTotal.textContent = fmt(estimatedTotal);
        }
      }
    }
    _completeAndAdvance(currentStep) {
      if (!this._isStepComplete(currentStep)) return;

      // Collapse current step
      const currentItem = this._accordionItems?.[currentStep - 1];
      const currentButton = currentItem?.querySelector('button.per-flow-button');
      if (currentButton?.getAttribute('aria-expanded') === 'true') {
        currentButton.setAttribute('aria-expanded', 'false');
      }

      // Open next incomplete step
      for (let next = currentStep + 1; next <= 4; next++) {
        if (!this._isStepComplete(next)) {
          this._goToStep(next);
          return;
        }
      }
      if (currentStep === 4) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this._repositionAfterAddonCollapse());
        });
      }
    }
  }
  customElements.define('personalisation-engine', PersonalisationEngine);
}
